import { useState } from 'react';
import { Trophy, Users, Send, ThumbsUp, ThumbsDown, Star, Clock, ChevronDown, ChevronUp, Shield, X, Music, CheckCircle, XCircle, Inbox } from 'lucide-react';
import type { AttendeeProfile, VoteRecord } from '../../types/analytics';
import type { SongRequest } from '../../services/requests';
import { GENRE_COLORS } from '../../utils/analyticsUtils';

type SortField = 'engagementScore' | 'requestCount' | 'upvoteCount' | 'nickname';

interface Props {
  profiles: AttendeeProfile[];
  requests?: SongRequest[];
  votes?: VoteRecord[];
}

export default function AttendeeProfiles({ profiles, requests = [], votes = [] }: Props) {
  const [showAll, setShowAll] = useState(false);
  const [sortField, setSortField] = useState<SortField>('engagementScore');
  const [selectedProfile, setSelectedProfile] = useState<AttendeeProfile | null>(null);

  const sorted = [...profiles].sort((a, b) => {
    if (sortField === 'nickname') return a.nickname.localeCompare(b.nickname);
    return (b[sortField] as number) - (a[sortField] as number);
  });

  const displayed = showAll ? sorted : sorted.slice(0, 5);

  // Build detail data for selected profile
  const profileRequests = selectedProfile
    ? requests.filter(r => r.requestedBy.oderId === selectedProfile.attendeeId)
    : [];
  const profileVotes = selectedProfile
    ? votes.filter(v => v.oderId === selectedProfile.attendeeId)
    : [];
  const profileUpvotes = profileVotes.filter(v => v.type === 'up');
  const profileDownvotes = profileVotes.filter(v => v.type === 'down');

  // Map requestId to song info for voted songs
  const requestMap = new Map(requests.map(r => [r.id, r]));

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-500" />
          Attendee Activity
          <span className="text-sm font-normal text-gray-500">({profiles.length})</span>
        </h3>
        <select
          value={sortField}
          onChange={(e) => setSortField(e.target.value as SortField)}
          className="input text-sm"
          aria-label="Sort attendee profiles"
        >
          <option value="engagementScore">Sort by Engagement</option>
          <option value="requestCount">Sort by Requests</option>
          <option value="upvoteCount">Sort by Votes</option>
          <option value="nickname">Sort by Name</option>
        </select>
      </div>

      {profiles.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>No attendee activity yet</p>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {displayed.map((profile) => {
              const globalIndex = sorted.indexOf(profile);
              const isTopThree = sortField === 'engagementScore' && globalIndex < 3;
              return (
                <button
                  key={profile.attendeeId}
                  onClick={() => setSelectedProfile(profile)}
                  className={`text-left bg-white dark:bg-gray-900 rounded-lg p-4 border transition-colors hover:border-primary/50 hover:shadow-sm cursor-pointer ${
                    isTopThree
                      ? 'border-amber-200 dark:border-amber-700'
                      : 'border-gray-100 dark:border-gray-800'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    {isTopThree && (
                      <span className="text-lg">
                        {globalIndex === 0 ? '🥇' : globalIndex === 1 ? '🥈' : '🥉'}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate flex items-center gap-1">
                        {profile.nickname}
                        {profile.isVerified && (
                          <Shield className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                        )}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Score: {profile.engagementScore}
                      </p>
                    </div>
                    {profile.topGenre !== 'N/A' && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full text-white flex-shrink-0"
                        style={{ backgroundColor: GENRE_COLORS[profile.topGenre] || GENRE_COLORS['Other'] }}
                      >
                        {profile.topGenre}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded p-1.5">
                      <p className="text-gray-500 dark:text-gray-400 flex items-center justify-center gap-0.5">
                        <Send className="w-3 h-3" /> Reqs
                      </p>
                      <p className="font-bold text-gray-900 dark:text-white">{profile.requestCount}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded p-1.5">
                      <p className="text-gray-500 dark:text-gray-400 flex items-center justify-center gap-0.5">
                        <ThumbsUp className="w-3 h-3" /> Votes
                      </p>
                      <p className="font-bold text-gray-900 dark:text-white">{profile.upvoteCount + profile.downvoteCount}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded p-1.5">
                      <p className="text-gray-500 dark:text-gray-400 flex items-center justify-center gap-0.5">
                        <Star className="w-3 h-3" /> Ranked
                      </p>
                      <p className="font-bold text-gray-900 dark:text-white">{profile.rankingCount}</p>
                    </div>
                  </div>

                  {profile.firstActiveTime && (
                    <div className="mt-2 text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(profile.firstActiveTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {profile.lastActiveTime && profile.lastActiveTime !== profile.firstActiveTime && (
                        <> — {new Date(profile.lastActiveTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {profiles.length > 5 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="mt-3 w-full text-center text-sm text-primary hover:text-primary/80 flex items-center justify-center gap-1 py-2"
            >
              {showAll ? (
                <>Show Less <ChevronUp className="w-4 h-4" /></>
              ) : (
                <>Show All {profiles.length} Attendees <ChevronDown className="w-4 h-4" /></>
              )}
            </button>
          )}
        </>
      )}

      {/* Attendee Detail Modal */}
      {selectedProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedProfile(null)}>
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                  {selectedProfile.nickname}
                  {selectedProfile.isVerified && <Shield className="w-4 h-4 text-green-500" />}
                </h3>
                {selectedProfile.topGenre !== 'N/A' && (
                  <span
                    className="text-xs px-2 py-0.5 rounded-full text-white"
                    style={{ backgroundColor: GENRE_COLORS[selectedProfile.topGenre] || GENRE_COLORS['Other'] }}
                  >
                    {selectedProfile.topGenre}
                  </span>
                )}
              </div>
              <button onClick={() => setSelectedProfile(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-5">
              {/* Stats Summary */}
              <div className="grid grid-cols-4 gap-3 text-center text-xs">
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2">
                  <p className="text-gray-500">Requests</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{selectedProfile.requestCount}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2">
                  <p className="text-green-500">Upvotes</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{selectedProfile.upvoteCount}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2">
                  <p className="text-red-500">Downvotes</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{selectedProfile.downvoteCount}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2">
                  <p className="text-amber-500">Rankings</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{selectedProfile.rankingCount}</p>
                </div>
              </div>

              {/* Engagement & Session */}
              <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                <span>Engagement Score: <strong className="text-gray-900 dark:text-white">{selectedProfile.engagementScore}</strong></span>
                {selectedProfile.firstActiveTime && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {new Date(selectedProfile.firstActiveTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {selectedProfile.lastActiveTime && selectedProfile.lastActiveTime !== selectedProfile.firstActiveTime && (
                      <> — {new Date(selectedProfile.lastActiveTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
                    )}
                  </span>
                )}
              </div>

              {/* Songs Requested */}
              {profileRequests.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                    <Music className="w-4 h-4" /> Songs Requested ({profileRequests.length})
                  </h4>
                  <div className="space-y-1.5">
                    {profileRequests.map(r => (
                      <div key={r.id} className="flex items-center gap-2 text-sm p-2 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                        {r.song.albumArtUrl ? (
                          <img src={r.song.albumArtUrl} alt="" className="w-8 h-8 rounded flex-shrink-0" />
                        ) : (
                          <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded flex-shrink-0 flex items-center justify-center">
                            <Music className="w-4 h-4 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-900 dark:text-white truncate text-xs font-medium">{r.song.title}</p>
                          <p className="text-gray-500 truncate text-xs">{r.song.artist}</p>
                        </div>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5 ${
                          r.status === 'played' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                          : r.status === 'queued' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                          : r.status === 'rejected' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                          : r.status === 'nowPlaying' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}>
                          {r.status === 'played' && <CheckCircle className="w-3 h-3" />}
                          {r.status === 'rejected' && <XCircle className="w-3 h-3" />}
                          {r.status === 'pending' && <Inbox className="w-3 h-3" />}
                          {r.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Songs Upvoted */}
              {profileUpvotes.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                    <ThumbsUp className="w-4 h-4 text-green-500" /> Upvoted ({profileUpvotes.length})
                  </h4>
                  <div className="space-y-1">
                    {profileUpvotes.map(v => {
                      const req = requestMap.get(v.requestId);
                      return (
                        <div key={`${v.requestId}-up`} className="flex items-center gap-2 text-xs p-1.5 rounded bg-green-50 dark:bg-green-900/10">
                          <ThumbsUp className="w-3 h-3 text-green-500 flex-shrink-0" />
                          <span className="text-gray-900 dark:text-white truncate">
                            {req ? `${req.song.title} — ${req.song.artist}` : v.requestId}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Songs Downvoted */}
              {profileDownvotes.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                    <ThumbsDown className="w-4 h-4 text-red-500" /> Downvoted ({profileDownvotes.length})
                  </h4>
                  <div className="space-y-1">
                    {profileDownvotes.map(v => {
                      const req = requestMap.get(v.requestId);
                      return (
                        <div key={`${v.requestId}-down`} className="flex items-center gap-2 text-xs p-1.5 rounded bg-red-50 dark:bg-red-900/10">
                          <ThumbsDown className="w-3 h-3 text-red-500 flex-shrink-0" />
                          <span className="text-gray-900 dark:text-white truncate">
                            {req ? `${req.song.title} — ${req.song.artist}` : v.requestId}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No detailed data available */}
              {profileRequests.length === 0 && profileVotes.length === 0 && (
                <div className="text-center py-4 text-sm text-gray-500">
                  This attendee only participated through rankings.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
